import { computed, onUnmounted, ref, shallowRef, watch } from 'vue'
import type { SearchResponse } from '@algolia/client-search'
import type { AlgoliaClient } from '@/search/algolia_client'
import { useBrowseState } from '@/browse/useBrowseState'
import { buildQueries, facetCountsFromResults } from '@/browse/browse_search'
import type { BrowseSearchConfig, FacetCounts } from '@/browse/browse_search'

export function useBrowseSearch<THit>(config: BrowseSearchConfig, getClient: () => Promise<AlgoliaClient>) {
  const browseState = useBrowseState(config)
  const { state } = browseState

  const plan = computed(() => config.searchPlan(state.value))
  const queries = computed(() => buildQueries(config, state.value, plan.value))
  const isReadyForSearch = computed(() => config.isReadyForSearch?.() ?? true)
  const hasSearchCriteria = computed(() => config.hasSearchCriteria?.(state.value) ?? true)
  // The query callbacks may need the data readiness is waiting for, so they are not run before it.
  const queriesFingerprint = computed(() => (isReadyForSearch.value && hasSearchCriteria.value ? JSON.stringify(queries.value) : ''))

  const hits = shallowRef<THit[]>([])
  const totalHits = ref(0)
  const queryID = ref('')
  const facetCounts = shallowRef<FacetCounts>({})
  const isLoading = ref(true)
  const hasFailed = ref(false)

  // Only the newest search may update the page, so a slow response to an older query
  // cannot replace newer results.
  let latestSearchId = 0
  function startSearch(): number {
    latestSearchId += 1
    return latestSearchId
  }
  function discardPendingSearches() {
    latestSearchId += 1
  }

  async function search() {
    if (!isReadyForSearch.value) {
      discardPendingSearches()
      isLoading.value = true
      return
    }
    if (!hasSearchCriteria.value) {
      discardPendingSearches()
      isLoading.value = false
      return
    }
    const searchId = startSearch()
    const isLatestSearch = () => searchId === latestSearchId
    const searchPlan = plan.value
    isLoading.value = true
    hasFailed.value = false
    try {
      const client = await getClient()
      const { results } = await client.multipleQueries<THit>(queries.value, { strategy: 'none' })
      if (!isLatestSearch()) {
        return
      }
      const [hitsResult] = results as SearchResponse<THit>[]
      hits.value = hitsResult.hits
      totalHits.value = hitsResult.nbHits
      queryID.value = hitsResult.queryID ?? ''
      facetCounts.value = facetCountsFromResults(config, searchPlan, results as SearchResponse<THit>[])
    } catch (e) {
      if (isLatestSearch()) {
        console.error('Browse search failed', e)
        hasFailed.value = true
      }
    } finally {
      if (isLatestSearch()) {
        isLoading.value = false
      }
    }
  }

  watch([isReadyForSearch, queriesFingerprint], search, { immediate: true })
  onUnmounted(discardPendingSearches)

  return { ...browseState, hits, totalHits, queryID, facetCounts, isLoading, hasFailed }
}
